import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, RefreshCcw, Plus, Award, Search, X } from "lucide-react";
import { Button } from "components/ui/button";

const EditLeadsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50">
    <div className="rounded-lg shadow-lg w-[90%] max-w-lg p-4 relative animate-slide-in-up">
      <div className="flex justify-between items-center mb-2">
        <h5 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Create New Lead</h5>
        <Button onClick={onClose} variant="ghost" size="icon" className="text-content-muted hover:text-gray-700 dark:hover:text-gray-300 h-6 w-6">✕</Button>
      </div>
      <div className="p-2 text-sm text-content-secondary"><p>(EditLeads modal placeholder — replace later)</p></div>
    </div>
  </div>
);

interface LeadsNodeProps {
  node: any;
  leads: any[];
  isOpen: boolean;
  isRefreshing?: boolean;
  onToggleAccordion: () => void;
  onDragStartLead?: (event: React.DragEvent, lead: any) => void;
  onAddNode: (nodeData: any) => void;
  onRefreshLeads: () => void;
}

const LeadsNode: React.FC<LeadsNodeProps> = ({
  node, leads, isOpen, isRefreshing = false,
  onToggleAccordion, onDragStartLead, onAddNode, onRefreshLeads,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [search, setSearch] = useState("");

  const activeLeads = useMemo(() => leads.filter(l => l.status === "ACTIVE"), [leads]);

  const filtered = useMemo(
    () => search.trim()
      ? activeLeads.filter(l => l.name?.toLowerCase().includes(search.toLowerCase()))
      : activeLeads,
    [activeLeads, search]
  );

  return (
    <div className="border border-line dark:border-white/[0.08] rounded-xl overflow-hidden shadow-sm bg-surface dark:bg-dark-800 mt-2">

      {/* Header */}
      <div
        onClick={onToggleAccordion}
        className="flex justify-between items-center px-3 py-2.5 cursor-pointer
                   bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.07]
                   border-b border-line dark:border-white/[0.06] transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200 text-sm">
          <Award className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
          <span>{node.name}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            onClick={e => { e.stopPropagation(); onRefreshLeads(); }}
            variant="ghost" size="icon"
            className="text-content-subtle dark:text-gray-200 hover:text-emerald-500 dark:hover:text-emerald-400 h-7 w-7"
            title="Refresh Leads"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
          {isOpen && (
            <Button
              onClick={e => { e.stopPropagation(); setIsModalVisible(true); }}
              variant="ghost" size="icon"
              className="h-7 w-7 text-content-subtle dark:text-gray-300 hover:text-emerald-500 dark:hover:text-emerald-400"
              title="Create New Lead"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
          {isOpen ? <ChevronUp className="h-4 w-4 text-content-subtle dark:text-gray-200 ml-0.5" />
                  : <ChevronDown className="h-4 w-4 text-content-subtle dark:text-gray-200 ml-0.5" />}
        </div>
      </div>

      {/* Body */}
      {isOpen && (
        <div className="p-2 space-y-1.5">

          {/* Search + count */}
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-content-subtle dark:text-gray-600 pointer-events-none" />
              <input
                type="text" placeholder="Search…" value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-6 pr-5 py-1 text-[11px] rounded-md
                           bg-gray-50 dark:bg-white/[0.05]
                           border border-line dark:border-white/[0.08]
                           text-gray-700 dark:text-gray-300
                           placeholder-gray-400 dark:placeholder-gray-600
                           focus:outline-none focus:ring-1 focus:ring-emerald-400/60
                           transition-colors"
              />
              {search && (
                <button onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary dark:hover:text-gray-200">
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
            {!isRefreshing && (
              <span className="flex-shrink-0 text-[9.5px] text-content-subtle dark:text-gray-600 tabular-nums">
                {filtered.length}/{activeLeads.length}
              </span>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto rounded-md border border-line-subtle dark:border-white/[0.06]
                          [&::-webkit-scrollbar]:w-[3px]
                          [&::-webkit-scrollbar-thumb]:rounded-full
                          [&::-webkit-scrollbar-thumb]:bg-emerald-300/40
                          dark:[&::-webkit-scrollbar-thumb]:bg-emerald-600/40"
               style={{ maxHeight: 168 }}>
            {isRefreshing ? (
              (["68%", "82%", "54%", "76%"] as const).map((w, i) => (
                <div key={i} className="sidebar-skeleton-row border-b border-line-subtle dark:border-white/[0.04] last:border-0">
                  <div className="sidebar-skeleton-line" style={{ width: w }} />
                  <div className="sidebar-skeleton-dot" />
                </div>
              ))
            ) : filtered.length > 0 ? (
              filtered.map((lead, idx) => (
                <div
                  key={lead.id || lead.name}
                  draggable
                  onDragStart={e => {
                    const nodeData = { name: lead.name, type: "lead" };
                    e.dataTransfer.setData("application/json", JSON.stringify(nodeData));
                    onDragStartLead?.(e, lead);
                  }}
                  className={`group flex items-center justify-between px-2 py-[5px]
                             hover:bg-emerald-50 dark:hover:bg-emerald-900/10
                             cursor-grab active:cursor-grabbing transition-colors
                             ${idx < filtered.length - 1 ? "border-b border-line-subtle dark:border-white/[0.04]" : ""}`}
                >
                  <span className="text-micro font-medium text-content-secondary dark:text-gray-300 truncate leading-none">
                    {lead.name}
                  </span>
                  <button
                    onClick={() => onAddNode({ name: lead.name, type: "lead" })}
                    title="Add to canvas"
                    className="flex-shrink-0 ml-1.5 w-[18px] h-[18px] rounded-full flex items-center justify-center
                               opacity-80 group-hover:opacity-100
                               bg-gray-100 dark:bg-white/[0.08]
                               group-hover:bg-emerald-500 dark:group-hover:bg-emerald-600
                               text-content-muted dark:text-gray-400 group-hover:text-white transition-all"
                  >
                    <Plus className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[11px] text-content-subtle dark:text-gray-600 italic text-center py-4">
                {search ? `No results for "${search}"` : "No active leads found."}
              </p>
            )}
          </div>

        </div>
      )}

      {isModalVisible && <EditLeadsModal onClose={() => setIsModalVisible(false)} />}
    </div>
  );
};

export default LeadsNode;
